import * as React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import styled from "styled-components"
import useLottie from "lottie-react";
import parse from "html-react-parser"
import { useStaticQuery, graphql, Link } from "gatsby"

import Layout from "../components/layout"
import Seo from "../components/seo"
import animationData from "../animation/resetAnimatedWhite.json";
import OutlineButton from "../components/buttons/outlineButton";

const HeroSection = styled.section`
  background-color: #6C7E99;
  background-image: url("../images/reset/reset-hero-bg.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  position: relative;
  margin-top: -66px;
  padding-top: 66px;

  @media (max-width: 756px) {
    margin-top: 0;
    padding-top: 3rem;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`

const HeroInner = styled.div`
  max-width: calc(1160px - 2rem);
  margin: 0 auto;
  padding: 1rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ResetLogo = styled.div`
  width: 210px;
  max-width: 100%;
  margin-bottom: 0;
  min-height: 125px;
`

const HeroHeading = styled.h1`
  color: #FFFFFF;
  font-size: clamp(2.625rem, 1.7679rem + 4.2857vw, 4.875rem);
  font-weight: 700;
  font-family: 'Kepler';
  line-height: 1.1;
  margin-bottom: 1rem;
  text-align: center;

  > span {
    display: block;

    > span {
      text-transform: uppercase;
    }
  }

  sup {
    vertical-align: top;
    position: relative;
    top: 0.4em;
  }
`

const HeroCTA = styled(OutlineButton)`
  border-color: #24293C;
  background-color: #24293C;
  border-radius: 0;
  color: #DAE0EE;
  margin-bottom: 3rem !important;
  display: inline-block;
  font-size: 18px;
  font-weight: 500;
  padding: 0.5em 2em;
  text-transform: none;

  &:hover, &:focus {
    background-color: #DAE0EE;
    color: #222A3F;
  }
`

const PageNav = styled.nav`
  display: flex;
  gap: 2px;
  background-color: #DAE0EE;
  justify-content: stretch;
  min-height: 150px;
  box-shadow: 0px 8px 10px 0px #0000000D;
  position: relative;
  z-index: 2;

  @media (max-width: 756px) {
    flex-direction: column;
    min-height: 0;
  }
`

const PageNavItem = styled.a`
  flex: 1 33%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  background-image: none;
  color: #24293C;
  font-size: clamp(1.25rem, 0.9191rem + 0.8824vw, 1.625rem);
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  text-align: center;
  line-height: 1.2;
  text-decoration: none;

  &:hover, &:focus {
    background-color: #24293C;
    color: #FFF;
  }

  span {
    text-transform: uppercase;
  }

  @media (max-width: 756px) {
    padding: 1rem;
  }
`


const IntroSection = styled.section`
  background-color: #F5F5F5;
`

const IntroSectionInner = styled.div`
  max-width: calc(1160px - 2rem);
  margin: 0 auto;
  padding: 5rem 1rem;
  display: flex;
  gap: 2rem;
  color: #24293C;
  font-size: clamp(1rem, 0.7243rem + 0.7353vw, 1.3125rem);
  align-items: center;
  font-weight: 400;
  line-height: 1.5;

  > div {
    flex: 1 50%;
  }

  @media (max-width: 756px) {
    flex-direction: column;
    padding: 3rem 1rem;

    > div:first-of-type {
      text-align: center;
    }
  }

  div:first-of-type {
    font-size: clamp(1.5rem, 1.1691rem + 0.8824vw, 1.875rem);
  }
`

const GeneralPriceStatement = styled.p`
  font-size: clamp(1.125rem, 0.9044rem + 0.5882vw, 1.375rem);
  line-height: 1.5;
  max-width: 925px;
  margin: -2rem auto 2rem;
  padding-bottom: 1.5rem;
  text-align: center;
`;

const MediaBlockSection = styled.section`
  background-color: #fff;
`

const MediaBlockSectionInner = styled.div`
  max-width: calc(1160px - 2rem);
  margin: 0 auto;
  padding: 3rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`

const MediaBlock = styled.div`
  scroll-margin-top: calc(66px + 2rem);
  display: flex;
  align-items: center;
  gap: 2rem;
  color: #4e4f51;
  font-size: clamp(1rem, 0.7243rem + 0.7353vw, 1.3125rem);

  h2 {
    color: #6c7e99;
    font-size: clamp(1.5rem, 1.0037rem + 1.3235vw, 2.0625rem);
    font-weight: 500;
    margin-bottom: 0.25rem;
    
    span {
      text-transform: uppercase;
    }

    sup {
      vertical-align: top;
      position: relative;
      top: 0.4em;
    }
  }

  > div {
    flex: 1 50%;
  }

  @media (max-width: 756px) {
    flex-direction: column;
    scroll-margin-top: 2rem;

    h2 {
      text-align: center;
    }
  }

  @media (min-width: 757px) {
    &:nth-child(even) {
      > div:first-child {
        order: 2;
      }
    }
  }
`

const PriceRange = styled.p`
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 0;
  color: #6c7e99;
`;

const PriceDisclaimer = styled.p`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 1rem;
  color: #666;
`;

const MediaSideBySide = styled.div`
  display: flex;
  gap: 0.5rem;
`

const LearnMoreSection = styled.section`
  padding: 3rem 1rem;
  background-color: #252A3D;
  background-image: url("../images/reset/learn-more-bg.png");
  background-repeat: no-repeat;
  background-position: left top;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h2 {
    color: #fff;
    font-size: clamp(1.875rem, -0.1103rem + 5.2941vw, 4.125rem);
    font-weight: 500;
    text-align: center;
    margin-bottom: 1rem;
  }
`

const LearnMoreCTA = styled(OutlineButton)`
  &[class] {
    border-color: #6A7792;
    background-color: #6A7792;
    border-radius: 0;
    color: #FFF;
    margin-bottom: 2rem !important;
    display: inline-block;
    font-size: 18px;
    font-weight: 500;
    padding: 0.5em 2em;
    text-transform: none;

    &:hover, &:focus {
      background-color: #DAE0EE;
      color: #222A3F;
    }
  }
`

const ResetArchive = data => {
  const page = data.page;

  const meta = useStaticQuery(graphql`
  query ResetArchiveQuery {
    page: wpPage(databaseId: {eq: 9726}) {
      resetArchive {
        resetHero {
          heading
          button {
            label
            url
          }
        }
        resetColumnSection {
          left
          right
        }
        generalPriceStatement
        resetTreatments {
          heading
          priceRange
          priceDisclaimer
          text
          url
          image {
            before {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 600, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
            after {
              node {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 600, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                  }
                }
              }
            }
          }
        }
        resetCta {
          heading
          button {
            label
            url
          }
        }
      }
    }
  }
  `)

  const reset = meta.page.resetArchive;

  const hero = reset.resetHero;
  const columnSection = reset.resetColumnSection;
  const generalPriceStatement = reset.generalPriceStatement;
  const treatments = reset.resetTreatments;
  const cta = reset.resetCta;
  const lottieRef = React.useRef();

  const Lottie = useLottie({
    animationData:animationData,
    loop:false,
    lottieRef: lottieRef
  });

  React.useEffect(() => {
    let playing = true;
    const playReset = () => {
      if (playing) {
        setTimeout(() => {
          playing = false;
        }, 5000);
      }
    }
  
    const lottiePlay = (event) => {
      const target = event.target;
      const parent = target.closest('.animate-on-hover');
  
      if (! playing && ( parent || ( target.classList && target.classList.contains('animate-on-hover') ) ) ) {
        playing = true;
        lottieRef.current.goToAndPlay(0);
        playReset();
      }
    }

    window.addEventListener('mouseover', lottiePlay);
    playReset();
  }, [ lottieRef ]);

  const scrollTo = (e) => {
    e.preventDefault();
    const target = e.target;
    const href = target.getAttribute('href');
    const element = document.querySelector(href);
    
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  return (
    <Layout>
      <Seo title={page.seo.title} description={page.seo.metaDesc} seo={page.seo} />
      <style
        dangerouslySetInnerHTML={
          {
            __html: `
              .page-header:not(.sticky){
                box-shadow: none;
              }
              .page-header:not(.sticky),
              .page-header:not(.sticky) #header-menu-toggle {
                background-color: transparent;
                color: #fff;
              }

              .page-header:not(.sticky) #header-menu-toggle > span > span { 
                background-color: #fff;
              }

              @media (min-width: 1204px) {
                .page-header:not(.sticky) *:not(#header-menu-explore, #header-menu-explore-toggle, #search-toggle) {
                  background-color: transparent;
                  color: #fff;
                }
                .page-header:not(.sticky) #header-menu-explore-toggle,
                .page-header:not(.sticky) #header-menu-explore-toggle span,
                .page-header:not(.sticky) #search-toggle {
                  filter: invert(1);
                }
              }

              .page-header[class]:not(.sticky) .main-logo__text[class][class]:not(.sticky) {
                fill: #fff !important;
              }

              .page-header:not(.sticky) #header-menu-explore li a {
                color: rgb(34, 42, 63);
              }

              .page-header:not(.sticky) #search-toggle {
                filter: invert(1);
              }
            `
          }
        }
      />
      <HeroSection>
        <HeroInner>
          <ResetLogo className="animate-on-hover">
            {Lottie}
          </ResetLogo>
          <HeroHeading>{parse(hero.heading || '')}</HeroHeading>
          { hero.button && <HeroCTA to={hero.button.url || ''}>{hero.button.label || ''}</HeroCTA>}
        </HeroInner>
      </HeroSection>
      <PageNav>
        {
          treatments.map((treatment, index) => { return(
            <PageNavItem 
              href={`#treatment-item-${index}`}
              onClick={scrollTo} 
              key={`treatment-menu-item-${index}`}
            >
              {treatment.heading}
            </PageNavItem>
          )})
        }
      </PageNav>
      <IntroSection>
        <IntroSectionInner>
          <div>{columnSection.left}</div>
          <div>{columnSection.right}</div>
        </IntroSectionInner>
        {generalPriceStatement &&
          <GeneralPriceStatement>
            {parse(generalPriceStatement)}
          </GeneralPriceStatement>
        }
      </IntroSection>
      <MediaBlockSection>
        <MediaBlockSectionInner>
          {treatments.map((treatment, index) => { return(
            <MediaBlock  id={`treatment-item-${index}`}>
              <div>
                <h2>
                  <Link to={treatment.url || ''}>
                    {parse(treatment.heading || '')}
                  </Link>
                </h2>
                {treatment.priceRange && 
                  <PriceRange>{treatment.priceRange}</PriceRange>  
                }
                {treatment.priceRange && treatment.priceDisclaimer && <PriceDisclaimer>{treatment.priceDisclaimer}</PriceDisclaimer>}
                {parse(treatment.text || '')}
              </div>
              <MediaSideBySide>
                {treatment.image && treatment.image.before && <GatsbyImage image={treatment.image.before.node.localFile.childImageSharp.gatsbyImageData} alt={treatment.image.before.node.altText || ''} />}
                {treatment.image && treatment.image.after && <GatsbyImage image={treatment.image.after.node.localFile.childImageSharp.gatsbyImageData} alt={treatment.image.after.node.altText || ''} />}
              </MediaSideBySide>
            </MediaBlock>
          )})}
        </MediaBlockSectionInner>
      </MediaBlockSection>
      <LearnMoreSection>
        <h2>{cta.heading}</h2>
        {cta.button && cta.button.url && <LearnMoreCTA to={cta.button.url}>{cta.button.label}</LearnMoreCTA>}
      </LearnMoreSection>
    </Layout>
  )
}

export default ResetArchive
